/**
 * Shared loader + typings for the Digio Web SDK v11.
 *
 * Script URL: `${DIGIO_DIGILOCKER_BASE_URL}/sdk/v11/digio.js`
 *   - production : https://app.digio.in
 *   - sandbox    : https://ext.digio.in  (our env uses https://ext-gateway.digio.in)
 * The base URL always comes from the injected env value (see src/config.ts) —
 * never hardcode a Digio host here.
 *
 * Usage (must match the SDK's call sequence):
 *   const digio = new (getDigioConstructor())({ ...options });
 *   digio.init();                                  // synchronous, inside the click handler
 *   digio.submit(documentId, customerIdentifier);  // after the create API resolves
 *   digio.cancel();                                // closes the iframe / popup
 *
 * The loader is idempotent: the <script> tag is injected at most once per page
 * load and is shared by every consumer. It is intentionally NOT removed on
 * unmount — removing the tag would not unload `window.Digio` and would break
 * other mounted consumers.
 */
import { useEffect, useState } from "react";
import { DIGIO_DIGILOCKER_BASE_URL } from "@/config";

// ─── SDK types (only options that actually exist in digio.js v11) ────────────

export interface DigioTheme {
	primaryColor?: string;
	secondaryColor?: string;
	fontFamily?: string;
	fontUrl?: string;
	fontFormat?: string;
}

/**
 * Shape of the object handed to `callback`.
 * Failure is signalled by the PRESENCE of `error_code`
 * (`error_code === "CANCELLED"` ⇒ the user closed the flow).
 * There is no `hasError` field in v11.
 */
export interface DigioCallbackResponse {
	txn_id?: string;
	digio_doc_id?: string;
	message?: string;
	error_code?: string;
}

/** Shape of the object handed to `event_listener`. */
export interface DigioEvent {
	documentId?: string;
	txnId?: string;
	entity?: string;
	identifier?: string;
	event?: string;
	payload?: {
		type?: "error" | "info";
		data?: unknown;
		error?: { code?: string; message?: string };
	};
}

// ─── eSign (entity: `sign`) event + error vocabulary ────────────────────────
// Shared here so every Digio consumer (eSign today, DigiCheck next) uses the
// same verbatim strings.

/**
 * The complete set of eSign gateway events, verbatim from the Digio gateway
 * bundle. Pass a MUTABLE COPY (`[...DIGIO_SIGN_EVENTS]`) as `event_filter`.
 *
 * WARNING: `sign.initiated` does NOT exist even though it appears in Digio's
 * own docs example — subscribing to it buys nothing and misleads readers.
 */
export const DIGIO_SIGN_EVENTS = [
	"sign.electronic.completed",
	"sign.electronic.failed",
	"sign.aadhaar.completed",
	"sign.aadhaar.failed",
	"sign.dsc.completed",
	"sign.dsc.failed",
	"sign.doc_review.accepted",
	"sign.doc_review.failed",
	"sign.doc_review.rejected",
] as const;

export type DigioSignEventName = (typeof DIGIO_SIGN_EVENTS)[number];

/**
 * Customer-facing copy for the Digio error codes that can arrive either as
 * `DigioCallbackResponse.error_code` or as `DigioEvent.payload.error.code`
 * (two different channels for the same vocabulary).
 *
 * `DOCUEMNT_NOT_READY_FOR_THIS_ACTION` is misspelled in Digio's own error
 * table — the key below matches the wire value exactly. Do not "fix" it.
 */
export const DIGIO_ERROR_MESSAGES: Record<string, string> = {
	CANCELLED: "You closed the signing window before it was completed.",
	ALREADY_SIGNED: "This document has already been signed.",
	SIGN_REQUEST_EXPIRED: "The signing request has expired. Please start again.",
	SIGNING_NOT_ALLOWED: "Signing is not allowed for this document right now.",
	UNAUTHORIZED_ACCESS: "You are not authorised to sign this document.",
	DUPLICATE_REQUEST: "A duplicate signing request was detected. Please try again.",
	DOCUEMNT_NOT_READY_FOR_THIS_ACTION:
		"The document is not ready for signing yet. Please try again in a moment.",
	REQUESTED_SIGN_METHOD_DIFFERENT:
		"A different signing method was requested for this document.",
	NOT_SUFFICIENT_ENTITLEMENTS: "Signing could not be completed due to a configuration issue.",
	INVALID_SIGNER: "The signer details do not match this document.",
	SYSTEM_ERROR: "The signing service reported a system error. Please try again.",
	UNKNOWN_ERROR: "The signing service reported an unknown error. Please try again.",
	INVALID_ELECTRONIC_SIGNATURE: "The electronic signature was not accepted. Please try again.",
	OTP_INVALID: "The OTP entered was incorrect. Please try again.",
	AADHAAR_HAS_NO_MOBILE:
		"No mobile number is linked to your Aadhaar, so Aadhaar eSign could not be completed.",
	SIGNING_TIMED_OUT_ON_ESP: "The signing session timed out. Please try again.",
	SIGNING_CANCELLED_ON_ESP: "Signing was cancelled on the Aadhaar eSign page.",
	AADHAAR_ESIGN_TEMPORARILY_UNAVAILABLE:
		"Aadhaar eSign is temporarily unavailable. Please try again shortly.",
	ERROR_FROM_ESP_UIDAI: "UIDAI reported an error during Aadhaar eSign. Please try again.",
};

/**
 * Log prefix for the DigiLocker KYC gateway's event stream.
 *
 * Deliberately paired with an UNFILTERED `event_listener` (no `event_filter`):
 * Digio does NOT document the KYC/DigiLocker gateway event names — only `sign.*`
 * and `nach.*` appear in their published event table. The bundle emits internal
 * analytics names such as `digilocker.page.load` / `digilocker.redirection`,
 * which are undocumented internals and MUST NOT be treated as API surface.
 * Logging every event behind one stable prefix lets UAT discover the real
 * vocabulary without us committing to it in code.
 */
export const DIGIO_KYC_EVENT_LOG_PREFIX = "[Digio KYC] event:";

/**
 * Best-effort customer-facing text for a Digio failure.
 * Priority: our mapped copy for a known code → Digio's own message → generic
 * text that still names the code so support can trace it.
 */
export const describeDigioError = (
	code?: string | null,
	message?: string | null,
	/**
	 * Only affects the LAST-RESORT generic sentence, so a non-signing consumer
	 * (e.g. the DigiLocker KYC flow) does not have to say "Signing failed" for a
	 * code we have no mapped copy for. The mapped table above is shared
	 * vocabulary and is intentionally unchanged; existing callers keep the
	 * "Signing" default.
	 */
	subject = "Signing",
): string => {
	if (code && DIGIO_ERROR_MESSAGES[code]) return DIGIO_ERROR_MESSAGES[code];
	if (message) return message;
	if (code) return `${subject} failed (${code}). Please try again.`;
	return `${subject} failed. Please try again.`;
};

// ─── Gateway access token (`submit()`'s optional 3rd argument) ───────────────

/**
 * Stable log prefix for every token-resolution decision. Deliberately one
 * prefix for all flows so UAT can grep a single string.
 */
export const DIGIO_TOKEN_LOG_PREFIX = "[Digio token]";

/**
 * Digio's gateway access tokens (`access_token.id` on the create response) are
 * documented as being prefixed `GWT`. We require the prefix DEFENSIVELY: a value
 * of some other shape is far more likely to be an unrelated id we would be
 * leaking into a URL than a usable token, and skipping it only costs us the
 * OTP-skip optimisation.
 */
const DIGIO_TOKEN_PREFIX = "GWT";

/**
 * digio.js v11 `generateUrl()` appends `token_id=<value>` WITHOUT
 * encodeURIComponent, so anything that is not URL-safe alphanumeric could break
 * the gateway URL (or smuggle extra query params into it). Reject such values
 * outright rather than encoding them ourselves — the gateway would not accept a
 * re-encoded token anyway.
 */
const DIGIO_TOKEN_CHARSET = /^[A-Za-z0-9]+$/;

/**
 * Safety margin subtracted from `valid_till` to absorb clock skew between the
 * user's device and Digio, plus the latency of the gateway request that will
 * consume the token. Digio's TTL is ~10 minutes, so 30s is negligible.
 */
export const DIGIO_TOKEN_EXPIRY_MARGIN_MS = 30 * 1000;

/**
 * Non-reversible description of a token for logs. The token is a BEARER
 * CREDENTIAL that bypasses the gateway's first factor, so its value must never
 * reach a log, `localStorage`/`sessionStorage`, or any URL we build ourselves.
 * Only the (public) prefix and the length are exposed.
 */
export const maskDigioToken = (tokenId: unknown): string =>
	typeof tokenId === "string" && tokenId.length > 0
		? `${DIGIO_TOKEN_PREFIX}***(len=${tokenId.length})`
		: "(none)";

/**
 * Normalises the several shapes `valid_till` may arrive in to epoch millis.
 *
 * Handled: ISO-8601 / any `Date`-parseable string, epoch SECONDS and epoch
 * MILLIS (as a number or as a numeric string). Returns null when the value is
 * absent or cannot be interpreted — callers treat null as "unknown expiry",
 * NOT as "expired".
 *
 * Seconds vs millis is disambiguated by magnitude: anything below 1e11 is
 * seconds (1e11 ms is 1973; 1e11 s is the year 5138), which is the standard
 * heuristic and unambiguous for any realistic token TTL.
 */
export const parseDigioTokenValidTill = (validTill: unknown): number | null => {
	try {
		if (validTill === null || validTill === undefined) return null;

		if (typeof validTill === "number") {
			if (!Number.isFinite(validTill) || validTill <= 0) return null;
			return validTill < 1e11 ? validTill * 1000 : validTill;
		}

		if (typeof validTill === "string") {
			const trimmed = validTill.trim();
			if (!trimmed) return null;
			// Numeric string → epoch seconds/millis.
			if (/^\d+(\.\d+)?$/.test(trimmed)) {
				const asNumber = Number(trimmed);
				if (!Number.isFinite(asNumber) || asNumber <= 0) return null;
				return asNumber < 1e11 ? asNumber * 1000 : asNumber;
			}
			const parsed = Date.parse(trimmed);
			return Number.isNaN(parsed) ? null : parsed;
		}

		return null;
	} catch {
		return null;
	}
};

/**
 * Decides whether Digio's gateway access token may be passed as the 3rd
 * argument of `digio.submit(id, identifier, tokenId)`.
 *
 * Passing a valid token makes the gateway log the user in by token instead of
 * rendering its own OTP/login screen. Passing a bad or expired one is SAFE for
 * our flows: the gateway's `checkNonIdentifierFlowAndRedirectOnTokenFailure()`
 * strips `token_id` from its URL and re-renders the normal OTP screen, provided
 * an `identifier` is present — and every one of our call sites passes a real
 * identifier. The token is therefore a pure optimisation; success is still
 * confirmed server-side by the calling flow.
 *
 * Returns `undefined` (never null, never throws) whenever the token must not be
 * used, so the caller's `submit(id, identifier, resolved)` is byte-for-byte
 * equivalent to today's 2-argument call.
 *
 * Rules:
 *   1. must be a non-empty string;
 *   2. must start with the documented `GWT` prefix;
 *   3. must be URL-safe alphanumeric (the SDK appends it un-encoded);
 *   4. must not already be past `validTill` minus a ~30s margin;
 *   5. an ABSENT/UNPARSEABLE `validTill` does not block the token (the gateway
 *      falls back to OTP by itself) but is logged as a warning.
 *
 * @param context short flow label used only in log lines (e.g. "eSign").
 */
export const resolveDigioToken = (
	tokenId: unknown,
	validTill: unknown,
	context = "Digio",
): string | undefined => {
	try {
		if (typeof tokenId !== "string" || tokenId.trim().length === 0) {
			console.log(
				`${DIGIO_TOKEN_LOG_PREFIX} ${context}: no token supplied — the gateway will ask for its own OTP. ` +
				`(present=false)`
			);
			return undefined;
		}

		const token = tokenId.trim();

		if (!token.startsWith(DIGIO_TOKEN_PREFIX)) {
			console.warn(
				`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token skipped — missing the documented "${DIGIO_TOKEN_PREFIX}" prefix. ` +
				`(present=true, ${maskDigioToken(token)})`
			);
			return undefined;
		}

		if (!DIGIO_TOKEN_CHARSET.test(token)) {
			console.warn(
				`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token skipped — not URL-safe alphanumeric, and the SDK appends ` +
				`token_id un-encoded. (present=true, ${maskDigioToken(token)})`
			);
			return undefined;
		}

		const expiresAt = parseDigioTokenValidTill(validTill);
		if (expiresAt === null) {
			console.warn(
				`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token used but its expiry could not be determined ` +
				`(valid_till type=${typeof validTill}). Proceeding — the gateway falls back to its OTP screen if the ` +
				`token is rejected. (present=true, ${maskDigioToken(token)})`
			);
			return token;
		}

		const usableUntil = expiresAt - DIGIO_TOKEN_EXPIRY_MARGIN_MS;
		if (Date.now() >= usableUntil) {
			console.warn(
				`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token skipped — expired or within the ` +
				`${DIGIO_TOKEN_EXPIRY_MARGIN_MS}ms safety margin (expired ${Date.now() - expiresAt}ms ago). ` +
				`The gateway will ask for its own OTP. (present=true, ${maskDigioToken(token)})`
			);
			return undefined;
		}

		console.log(
			`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token used — the gateway should skip its own OTP step. ` +
			`(present=true, ${maskDigioToken(token)}, expires_in_ms=${expiresAt - Date.now()})`
		);
		return token;
	} catch (e) {
		// Must never break a flow over an optimisation.
		console.warn(`${DIGIO_TOKEN_LOG_PREFIX} ${context}: token resolution threw — continuing without it.`, e);
		return undefined;
	}
};

export interface DigioOptions {
	environment?: "sandbox" | "production" | "stage";
	logo?: string;
	theme?: DigioTheme;
	callback?: (response: DigioCallbackResponse) => void;
	event_listener?: (event: DigioEvent) => void;
	/**
	 * MUST be a plain array of event names. digio.js v11 validates it with
	 * `event_filter.constructor.name !== 'Array'` (throwing a DigioException
	 * otherwise) and consumes it as `event_filter.includes(event.data.event)`.
	 * An object wrapper such as `{ events: [...] }` throws in the constructor.
	 */
	event_filter?: string[];
	/** Renders the flow in a full-viewport iframe overlay instead of a popup window. */
	is_iframe?: boolean;
	is_redirection_approach?: boolean;
	redirect_url?: string;
	error_url?: string;
	/** 1000–15000, default 2800. */
	redirect_timeout?: number;
	method?: "otp" | "biometric";
	/** Any `dg_*` key is forwarded to Digio as a query param (prefix stripped). */
	[dgKey: `dg_${string}`]: unknown;
}

export interface DigioInstance {
	init: () => void;
	submit: (documentId: string, identifier: string, tokenId?: string) => void;
	cancel: () => void;
	/** Assignable after construction, but prefer passing `callback` in the options. */
	callback?: (response: DigioCallbackResponse) => void;
}

export type DigioConstructor = new (options: DigioOptions) => DigioInstance;

/** Narrowly-typed accessor for the constructor that digio.js puts on `window`. */
export const getDigioConstructor = (): DigioConstructor | undefined =>
	(window as unknown as { Digio?: DigioConstructor }).Digio;

/**
 * Last-resort removal of a Digio iframe overlay that no instance owns any more.
 *
 * Why this is needed: in iframe mode `init()` appends its overlay container
 * (`id="parent" + iFrameId`, where `iFrameId = "digio-ifm-" + Date.now()`) to
 * `document.body` EARLY, and only assigns `this.popup` near the END of the same
 * function. So if `init()` throws anywhere in between:
 *   - the caller's `digioRef.current = digio` line never runs, so `cancel()`
 *     can never be reached through our own ref; and
 *   - `cancel()` would be a no-op anyway, because it only removes the container
 *     when `this.popup` is truthy.
 * The result is a `position: fixed; z-index: 999999` full-viewport curtain that
 * nothing can dismiss. Every init-threw / fallback path therefore sweeps up any
 * container matching that id prefix before degrading.
 *
 * Safe to call unconditionally: it only matches Digio's own generated ids, and
 * a live overlay is never orphaned (its owner holds a ref and uses `cancel()`).
 */
/**
 * The overlay container digio.js appends to `document.body` in iframe mode:
 * `id = "parent" + iFrameId`, `iFrameId = "digio-ifm-" + Date.now()`.
 * Matching on the prefix is the only stable handle we have.
 */
const DIGIO_OVERLAY_CONTAINER_SELECTOR = '[id^="parentdigio-ifm-"]';

/**
 * Attribute set on `<body>` for as long as a Digio overlay is on screen.
 *
 * WHY: the overlay deliberately ends DIGIO_OVERLAY_BOTTOM_GAP_PX above the
 * bottom of the screen so the gateway's own action row clears the mobile
 * browser chrome. Anything the host page pins to the bottom of the viewport
 * (JourneyPage's fixed "Continue" footer) therefore shows through that gap and
 * lands visually on top of the gateway's Cancel/Submit row — on iOS it covered
 * Submit outright, so the user could not complete the mandate.
 *
 * Raising the overlay's z-index cannot fix that: the footer is OUTSIDE the
 * overlay's box, in the gap, not behind it. The host page's bottom-pinned
 * chrome simply has no business being visible while a full-screen gateway is
 * up, so it is hidden by the CSS rule keyed on this attribute (see index.css).
 *
 * An attribute on <body> rather than React state because the overlay is created
 * by the SDK, not by React, and every Digio consumer (eSign, DigiLocker, UPI,
 * NACH) gets the behaviour for free.
 */
export const DIGIO_OVERLAY_BODY_ATTRIBUTE = "data-digio-overlay";

/**
 * Id of the opaque backdrop we insert BEHIND the Digio overlay.
 *
 * WHY: the overlay is only as tall as `viewport - DIGIO_OVERLAY_BOTTOM_GAP_PX`,
 * so the bottom strip is not covered by it. Hiding the host page's own fixed
 * footer stopped that footer covering the gateway's Submit button, but it left
 * the strip transparent — the page BEHIND the overlay (the next mandate card,
 * its input fields) then shows through underneath the gateway, which reads as
 * two stacked pages.
 *
 * The gap cannot simply be removed: it is what keeps the gateway's own
 * Cancel/Submit row clear of the mobile browser's bottom chrome.
 *
 * So the strip gets a plain opaque layer sitting one z-index below the overlay.
 * White because every Digio gateway page is white, making the seam invisible.
 */
const DIGIO_BACKDROP_ID = "digio-overlay-backdrop";

/** Removes the backdrop, if present. Safe to call unconditionally. */
const removeDigioBackdrop = (): void => {
	try {
		document.getElementById(DIGIO_BACKDROP_ID)?.remove();
	} catch { /* ignore */ }
};

/**
 * Inserts (or reuses) the full-viewport opaque layer behind the Digio overlay.
 * z-index is 999998 — exactly one below the overlay container's 999999, so the
 * gateway stays on top while everything of ours is hidden.
 */
const ensureDigioBackdrop = (): void => {
	try {
		if (document.getElementById(DIGIO_BACKDROP_ID)) return;
		const backdrop = document.createElement("div");
		backdrop.id = DIGIO_BACKDROP_ID;
		backdrop.setAttribute("aria-hidden", "true");
		backdrop.style.cssText =
			"position:fixed;inset:0;z-index:999998;background:#ffffff;" +
			// Taps in the strip must not reach the page underneath — that page is
			// mid-journey and a stray tap there would act on a screen the user
			// cannot even see.
			"pointer-events:auto;";
		document.body.appendChild(backdrop);
	} catch { /* ignore */ }
};

export const removeOrphanedDigioOverlays = (): number => {
	const orphans = document.querySelectorAll(DIGIO_OVERLAY_CONTAINER_SELECTOR);
	orphans.forEach((node) => node.remove());
	if (orphans.length > 0) {
		console.warn(`[Digio SDK] Removed ${orphans.length} orphaned Digio overlay container(s).`);
		// Restore body scroll when removing orphaned overlays — ensures the page
		// is scrollable again even if the cleanup handler wasn't called properly.
		try {
			document.body.style.removeProperty("overflow");
		} catch { /* ignore */ }
	}
	// Always clear the overlay-open flag and the backdrop, even when nothing was
	// orphaned: this is the sweep-up path for a torn-down overlay, and leaving
	// either behind would keep the host page's Continue button invisible / the
	// page covered by a white layer for the rest of the session.
	// See DIGIO_OVERLAY_BODY_ATTRIBUTE and DIGIO_BACKDROP_ID.
	try {
		document.body.removeAttribute(DIGIO_OVERLAY_BODY_ATTRIBUTE);
	} catch { /* ignore */ }
	removeDigioBackdrop();
	return orphans.length;
};

// ─── Overlay sizing (inline / `is_iframe: true` only) ────────────────────────

/**
 * Stable, greppable log prefix for every overlay-sizing decision.
 */
export const DIGIO_OVERLAY_LOG_PREFIX = "[Digio overlay]";

/**
 * Viewport value used while a Digio overlay is on screen.
 *
 * The app's normal viewport tag (index.html) allows pinch-zoom
 * (`user-scalable=yes, maximum-scale=5.0`). That is fine for our own pages, but
 * it breaks the Digio gateway: the gateway's inputs (e.g. the NACH IFSC field)
 * have a font-size under 16px, so focusing one makes mobile Safari/Chrome
 * PAGE-zoom. Page zoom shrinks the visual viewport, which
 *   (a) pushes our `position: fixed` overlay out of alignment, and
 *   (b) lets the page BEHIND the overlay show through / come to the front,
 * leaving the user unable to type into the field at all.
 *
 * Because the gateway is a cross-origin iframe we cannot restyle its inputs, so
 * the only lever is to disable zoom on the HOST page for as long as the overlay
 * is up. The original tag is restored on teardown, so the rest of the app keeps
 * its accessibility-friendly pinch-zoom.
 */
const DIGIO_LOCKED_VIEWPORT_CONTENT =
	"width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no";

/**
 * Disables page zoom for the lifetime of a Digio overlay. Returns a restore
 * function; calling it twice is safe.
 */
export const lockViewportZoom = (context = "Digio"): (() => void) => {
	try {
		const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
		if (!meta) return () => { /* no-op */ };

		const original = meta.getAttribute("content");
		if (meta.getAttribute("content") === DIGIO_LOCKED_VIEWPORT_CONTENT) {
			// Already locked by an outer flow — let that one own the restore.
			return () => { /* no-op */ };
		}
		meta.setAttribute("content", DIGIO_LOCKED_VIEWPORT_CONTENT);
		console.log(`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: page zoom locked for the overlay.`);

		let restored = false;
		return () => {
			if (restored) return;
			restored = true;
			if (original === null) meta.removeAttribute("content");
			else meta.setAttribute("content", original);
			console.log(`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: page zoom restored.`);
		};
	} catch (e) {
		console.warn(`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: viewport lock threw — continuing without it.`, e);
		return () => { /* no-op */ };
	}
};

/**
 * How much shorter the VISUAL viewport must be than the LAYOUT viewport before we
 * treat the difference as "a soft keyboard is open".
 *
 * Mobile browsers also shrink the visual viewport slightly for their own chrome
 * (collapsing URL bar, etc.), which is why this is not simply `> 0`. A real
 * keyboard eats 250-350px; browser chrome stays well under 150.
 */
const DIGIO_KEYBOARD_THRESHOLD_PX = 150;

/** How long fitDigioOverlayToViewport() waits for init() to append its container. */
const DIGIO_OVERLAY_WAIT_MS = 3000;

/** Backup polling cadence while waiting; the MutationObserver is primary. */
const DIGIO_OVERLAY_POLL_MS = 150;

/**
 * Extra breathing room, in CSS px, reserved BELOW the Digio iframe on top of the
 * OS safe-area inset.
 *
 * WHY: the Digio gateway pages (DigiLocker KYC, UPI mandate, NACH mandate) put
 * their primary submit button at the very bottom of their own scrollable
 * document. If our overlay's bottom edge lands underneath the mobile browser's
 * bottom chrome / home indicator, that last row is clipped and the button
 * renders half-visible even after the user scrolls the iframe all the way down.
 * Because the iframe is cross-origin we cannot scroll its content ourselves, so
 * the only reliable fix is to end the iframe's box ABOVE that hidden strip.
 *
 * Sized to clear the mobile browser's bottom chrome. 24px was not enough on
 * iOS Safari — the gateway's Cancel/Submit row still sat behind the toolbar, so
 * the user could see it but not tap it. Kept as small as possible beyond that,
 * because every px here is a px the gateway's document does not get, and its
 * action bar is sticky, so a large gap makes that bar cover more of its own
 * scrollable content.
 *
 * NOTE the clipped-button symptom originally chased in this file was NEVER an
 * insufficient-gap problem. It had two real causes, both fixed independently:
 * the STALE FIRST MEASUREMENT (see DIGIO_OVERLAY_RESIZE_RETRY_MS) and feeding
 * `visualViewport.offsetTop` into `top` (see the `top` assignment in
 * applySizing), which pushed the whole box off the bottom of the screen. Do not
 * inflate this constant trying to compensate for a regression in either of those.
 */
const DIGIO_OVERLAY_BOTTOM_GAP_PX = 72;

/**
 * Delays (ms after the overlay container is adopted) at which the sizing pass is
 * re-run.
 *
 * WHY: the FIRST measurement happens the instant digio.js appends its container,
 * which on mobile Safari/Chrome is BEFORE the browser has settled its collapsing
 * bottom chrome. At that moment `visualViewport.height` still reports the taller,
 * pre-collapse height, so the overlay's bottom edge lands UNDERNEATH the URL bar
 * and the gateway's own last row — its primary submit button ("Proceed" on the
 * DigiLocker consent screen, "Submit"/"Proceed" on the UPI/NACH mandate screens)
 * — renders half-clipped on load. Previously that only corrected itself once the
 * user happened to scroll/rotate and fire our resize listener.
 *
 * Re-measuring on a short schedule makes the correct size the DEFAULT instead of
 * something the user has to trigger. Each pass writes only the declarations that
 * actually differ (see applyImportantStyles), so extra passes are no-ops once the
 * viewport has settled.
 */
const DIGIO_OVERLAY_RESIZE_RETRY_MS = [50, 250, 600, 1200, 2500];

/**
 * Measures `env(safe-area-inset-bottom)` in CSS px (home indicator / bottom
 * chrome allowance), via a throwaway hidden probe element.
 *
 * There is no way to read an `env()` value from JS directly, so we let the
 * browser resolve it on a real element and read back the computed height.
 * Returns 0 on browsers without safe-area support, and never throws.
 */
const readSafeAreaBottomPx = (): number => {
	let probe: HTMLDivElement | null = null;
	try {
		probe = document.createElement("div");
		probe.setAttribute("aria-hidden", "true");
		probe.style.cssText =
			"position:fixed;left:0;bottom:0;width:0;visibility:hidden;pointer-events:none;" +
			"height:env(safe-area-inset-bottom,0px);";
		document.body.appendChild(probe);
		const measured = probe.getBoundingClientRect().height;
		return Number.isFinite(measured) && measured > 0 ? Math.round(measured) : 0;
	} catch {
		return 0;
	} finally {
		try { probe?.remove(); } catch { /* ignore */ }
	}
};

/** Minimal structural type for `window.visualViewport` (feature-detected). */
type DigioVisualViewport = {
	width?: number;
	height?: number;
	offsetLeft?: number;
	offsetTop?: number;
	addEventListener?: (type: string, listener: () => void) => void;
	removeEventListener?: (type: string, listener: () => void) => void;
};

const getVisualViewport = (): DigioVisualViewport | undefined =>
	(window as unknown as { visualViewport?: DigioVisualViewport | null }).visualViewport ?? undefined;

/** Writes only the declarations that actually differ. */
const applyImportantStyles = (element: HTMLElement, declarations: Array<[string, string]>): void => {
	declarations.forEach(([property, value]) => {
		if (element.style.getPropertyValue(property) === value) return;
		element.style.setProperty(property, value, "important");
	});
};

/**
 * Makes the Digio inline overlay match the DEVICE viewport instead of the
 * desktop-ish box the SDK sizes for.
 *
 * WHY THIS EXISTS: in iframe mode (`is_iframe: true`) digio.js builds its own
 * container + iframe and sets their dimensions INLINE, tuned for a desktop
 * window. On a phone that overflows horizontally, so the right-hand edge of the
 * gateway page (the last OTP box, the primary action button) is pushed off
 * screen. Because the SDK writes inline styles — and may re-write them later —
 * external CSS alone cannot win; this pass writes inline `!important` values and
 * re-asserts them on every viewport change.
 *
 * SIZING STRATEGY: the VISUAL viewport (`window.visualViewport`) with an
 * `innerWidth`/`innerHeight` fallback. `100vw` is deliberately never used — on
 * iOS it is WIDER than the visual viewport and is itself a cause of horizontal
 * overflow.
 *
 * Never throws, and the returned cleanup is idempotent.
 *
 * @param context short flow label used only in log lines (e.g. "NACH mandate").
 * @returns a cleanup that detaches every observer/timer/listener.
 */
export const fitDigioOverlayToViewport = (context = "Digio"): (() => void) => {
	try {
		let disposed = false;
		let container: HTMLElement | null = null;
		let bodyObserver: MutationObserver | null = null;
		let styleObserver: MutationObserver | null = null;
		let pollTimer: ReturnType<typeof setTimeout> | null = null;
		let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
		let frame: number | null = null;
		let warned = false;
		let retryTimers: ReturnType<typeof setTimeout>[] = [];

		// Kill page zoom for as long as the overlay is up. Focusing a sub-16px
		// input inside the cross-origin gateway (e.g. the NACH IFSC field) would
		// otherwise page-zoom the host, shrinking the visual viewport, knocking
		// this fixed overlay out of alignment and letting the page behind it show
		// through — at which point the user cannot type into the field at all.
		// Restored by the returned cleanup, so the rest of the app keeps pinch-zoom.
		const restoreViewportZoom = lockViewportZoom(context);

		const stopWaiting = () => {
			if (bodyObserver) { bodyObserver.disconnect(); bodyObserver = null; }
			if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }
			if (deadlineTimer !== null) { clearTimeout(deadlineTimer); deadlineTimer = null; }
		};

		const clearRetries = () => {
			retryTimers.forEach((timer) => clearTimeout(timer));
			retryTimers = [];
		};

		const applySizing = () => {
			if (disposed || !container) return;
			const viewport = getVisualViewport();
			const rawWidth  = typeof viewport?.width  === "number" ? viewport.width  : window.innerWidth;

			// KEYBOARD HANDLING.
			//
			// An older comment here claimed a soft keyboard "shifts ITS document, not
			// our overlay box". That is wrong on iOS: opening the keyboard shrinks
			// `visualViewport.height` by the keyboard's height.
			//
			// Sizing the overlay to that SHRUNKEN height is NOT an option: the page
			// behind stays full height, so the overlay collapses to the strip above
			// the keyboard and the app's own page (URL bar, "Customer Identifier"
			// field, autofill bar) shows through the gap underneath the Digio card —
			// the exact symptom this file has now regressed into twice. Do not
			// reintroduce it.
			//
			// So while the keyboard is up the overlay is HELD at the layout-viewport
			// height. Reaching the gateway's Cancel/Submit row is handled separately,
			// by DIGIO_OVERLAY_BOTTOM_GAP_PX (which lifts the whole box clear of the
			// browser's bottom chrome) plus the gateway's own in-iframe scrolling.
			const layoutHeight = window.innerHeight || 0;
			const visualHeight = typeof viewport?.height === "number" ? viewport.height : layoutHeight;
			const keyboardOpen = layoutHeight > 0 && (layoutHeight - visualHeight) > DIGIO_KEYBOARD_THRESHOLD_PX;

			// Keyboard up -> full layout height (never the shrunken visual height; see
			// above). Keyboard down -> the SMALLER of the visual viewport and
			// `innerHeight`, because on iOS `innerHeight` includes the strip hidden
			// behind the collapsed bottom chrome and `visualViewport.height` briefly
			// reports that same taller value right after the overlay is appended.
			const rawHeight = keyboardOpen
				? layoutHeight
				: (typeof viewport?.height === "number"
					? Math.min(viewport.height, layoutHeight || viewport.height)
					: layoutHeight);
			const rawLeft   = typeof viewport?.offsetLeft === "number" ? viewport.offsetLeft : 0;
			if (!Number.isFinite(rawWidth) || !Number.isFinite(rawHeight) || rawWidth <= 0 || rawHeight <= 0) return;
			const width  = Math.round(rawWidth);
			// End the overlay ABOVE the browser's bottom chrome / home indicator so the
			// gateway's submit button (last row of its own scroll) is never half-clipped.
			// See DIGIO_OVERLAY_BOTTOM_GAP_PX. Clamped so a small viewport can't collapse
			// the overlay to nothing.
			// Applied in BOTH states. It used to be skipped while the keyboard was up,
			// but the gap is what keeps the gateway's action row clear of the browser
			// chrome, and dropping it is why Submit became unreachable.
			const bottomInset = readSafeAreaBottomPx() + DIGIO_OVERLAY_BOTTOM_GAP_PX;
			const height = Math.max(Math.round(rawHeight) - bottomInset, Math.round(rawHeight * 0.6));
			const left   = Number.isFinite(rawLeft) ? Math.round(rawLeft) : 0;
			// `top` is PINNED TO 0 and deliberately ignores `visualViewport.offsetTop`.
			//
			// WHY: offsetTop is non-zero whenever the visual viewport is displaced from
			// the layout viewport — most commonly because the page behind was scrolled
			// when digio.js appended its container. Feeding it into a `position: fixed`
			// box pushed the overlay DOWN by that amount while keeping its full height,
			// so it hung off the bottom of the screen: a dead grey gap at the top (the
			// page behind showing through) and the gateway's action row clipped by the
			// very same number of px at the bottom.
			//
			// For `position: fixed` the containing block is the viewport already, so 0
			// is the correct top in every non-keyboard case — which is all of ours: the
			// gateway renders its inputs inside the iframe, so a soft keyboard shifts
			// ITS document, not our overlay box.
			const top    = 0;
			applyImportantStyles(container, [
				["position",   "fixed"],
				["left",       `${left}px`],
				["top",        `${top}px`],
				["width",      `${width}px`],
				["height",     `${height}px`],
				["max-width",  "100%"],
				["min-width",  "0"],
				["margin",     "0"],
				["padding",    "0"],
				["box-sizing", "border-box"],
				["overflow",   "auto"],      // Allow container itself to scroll
				["z-index",    "999999"],
			]);
			// Fix ALL intermediate wrapper divs the SDK injects between the
			// container and the iframe — they inherit desktop widths from the
			// SDK's own inline styles and clip the right edge on narrow viewports.
			const wrappers = container.querySelectorAll<HTMLElement>("div");
			wrappers.forEach((div) => {
				applyImportantStyles(div, [
					["width",      "100%"],
					["height",     "100%"],
					["max-width",  "100%"],
					["min-width",  "0"],
					["margin",     "0"],
					["padding",    "0"],
					["box-sizing", "border-box"],
					["overflow",   "visible"],  // Allow content to be scrollable
				]);
			});
			const iframe = container.querySelector("iframe");
			if (iframe instanceof HTMLIFrameElement) {
				// Force scrolling to yes/auto for all Digio iframes — the SDK may set
				// scrolling="no" which clips bottom content (buttons, NACH section).
				iframe.setAttribute("scrolling", "yes");
				// Exactly the container box. The gateway's own layout (header +
				// scrollable middle + sticky footer) then does the right thing by
				// itself: the footer stays pinned and the middle scrolls natively.
				// Do NOT oversize this — an iframe taller than its container can only
				// be scrolled via OUR container, and touch/wheel gestures over an
				// iframe go to the IFRAME's document, not to our element, so that
				// scroll is unreachable on a phone.
				applyImportantStyles(iframe, [
					["width",      "100%"],
					["height",     "100%"],
					["max-width",  "100%"],
					["min-width",  "0"],
					["border",     "0"],
					["display",    "block"],
					["margin",     "0"],
					["padding",    "0"],
					["overflow",   "auto"],     // Ensure iframe content can scroll
					["overflow-y", "scroll"],   // Force vertical scrollbar space
				]);
			}
		};

		const scheduleSizing = () => {
			if (disposed) return;
			if (frame !== null) return;
			frame = window.requestAnimationFrame(() => { frame = null; applySizing(); });
		};

		const handleViewportChange = () => scheduleSizing();

		const attachListeners = () => {
			window.addEventListener("resize", handleViewportChange);
			window.addEventListener("orientationchange", handleViewportChange);
			const viewport = getVisualViewport();
			viewport?.addEventListener?.("resize", handleViewportChange);
			viewport?.addEventListener?.("scroll", handleViewportChange);
		};

		const detachListeners = () => {
			window.removeEventListener("resize", handleViewportChange);
			window.removeEventListener("orientationchange", handleViewportChange);
			const viewport = getVisualViewport();
			viewport?.removeEventListener?.("resize", handleViewportChange);
			viewport?.removeEventListener?.("scroll", handleViewportChange);
		};

		const adopt = (found: HTMLElement) => {
			if (disposed) return;
			container = found;
			stopWaiting();
			// Re-measure on a short schedule — the first pass runs before mobile
			// browser chrome settles. See DIGIO_OVERLAY_RESIZE_RETRY_MS.
			DIGIO_OVERLAY_RESIZE_RETRY_MS.forEach((delay) => {
				retryTimers.push(setTimeout(() => { if (!disposed) applySizing(); }, delay));
			});
			// Send the page behind BACK TO THE TOP before freezing it.
			//
			// `overflow: hidden` alone only freezes the page wherever it already was.
			// If the user had scrolled down before triggering the flow, the visual
			// viewport stays displaced (non-zero `offsetTop`) and the page's own header
			// remains visible behind our overlay — the grey strip above the Digio card.
			// Resetting the scroll first makes the overlay's `top: 0` line up with what
			// the user actually sees.
			try {
				window.scrollTo(0, 0);
			} catch { /* ignore */ }
			// Lock body scroll so the page behind the overlay doesn't scroll
			// while the Digio iframe is open (prevents the overlay from appearing
			// to "float" above a scrolled page on iOS/Android).
			try {
				document.body.style.setProperty("overflow", "hidden", "important");
			} catch { /* ignore */ }
			// Flag the overlay as open so the host page can hide its own
			// bottom-pinned chrome, which would otherwise sit in the overlay's
			// bottom gap and cover the gateway's Submit button.
			// See DIGIO_OVERLAY_BODY_ATTRIBUTE.
			try {
				document.body.setAttribute(DIGIO_OVERLAY_BODY_ATTRIBUTE, "open");
			} catch { /* ignore */ }
			// Fill the strip below the overlay (and anything else it does not
			// cover) with an opaque layer, so the journey page behind never shows
			// through under the gateway. See DIGIO_BACKDROP_ID.
			ensureDigioBackdrop();
			applySizing();
			console.log(
				`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: overlay container #${found.id} sized to the visual viewport ` +
				`(${Math.round(getVisualViewport()?.width ?? window.innerWidth)}×` +
				`${Math.round(getVisualViewport()?.height ?? window.innerHeight)}px).`
			);
			attachListeners();
			try {
				styleObserver = new MutationObserver(() => { if (disposed) return; scheduleSizing(); });
				// Watch both style attribute changes AND child additions:
				// - attributes: SDK may re-write inline styles after init()
				// - childList/subtree: SDK appends wrapper divs + the iframe
				//   AFTER the container appears, so we must re-run applySizing
				//   once those children exist (otherwise the iframe width fix
				//   runs before the iframe element is in the DOM).
				styleObserver.observe(found, {
					attributes: true,
					attributeFilter: ["style"],
					childList: true,
					subtree: true,
				});
			} catch { styleObserver = null; }
		};

		const lookup = (): HTMLElement | null =>
			document.querySelector<HTMLElement>(DIGIO_OVERLAY_CONTAINER_SELECTOR);

		const existing = lookup();
		if (existing) {
			adopt(existing);
		} else {
			try {
				bodyObserver = new MutationObserver(() => {
					if (disposed || container) return;
					const found = lookup();
					if (found) adopt(found);
				});
				bodyObserver.observe(document.body, { childList: true, subtree: false });
			} catch { bodyObserver = null; }

			const poll = () => {
				if (disposed || container) return;
				const found = lookup();
				if (found) { adopt(found); return; }
				pollTimer = setTimeout(poll, DIGIO_OVERLAY_POLL_MS);
			};
			pollTimer = setTimeout(poll, DIGIO_OVERLAY_POLL_MS);

			deadlineTimer = setTimeout(() => {
				if (disposed || container) return;
				stopWaiting();
				if (!warned) {
					warned = true;
					console.warn(
						`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: no overlay container matched ` +
						`"${DIGIO_OVERLAY_CONTAINER_SELECTOR}" within ${DIGIO_OVERLAY_WAIT_MS}ms — skipping viewport sizing.`
					);
				}
			}, DIGIO_OVERLAY_WAIT_MS);
		}

		return () => {
			if (disposed) return;
			disposed = true;
			stopWaiting();
			clearRetries();
			if (styleObserver) { styleObserver.disconnect(); styleObserver = null; }
			if (frame !== null) { window.cancelAnimationFrame(frame); frame = null; }
			detachListeners();
			// Give the app its pinch-zoom back now that the gateway is gone.
			restoreViewportZoom();
			// Restore body scroll when the overlay is dismissed
			try {
				document.body.style.removeProperty("overflow");
				// Force a reflow to ensure scroll is restored immediately
				// (prevents the "stuck" scroll state on some mobile browsers)
				void document.body.offsetHeight;
			} catch { /* ignore */ }
			// Give the host page its bottom chrome (Continue button) back.
			try {
				document.body.removeAttribute(DIGIO_OVERLAY_BODY_ATTRIBUTE);
			} catch { /* ignore */ }
			removeDigioBackdrop();
			container = null;
		};
	} catch (e) {
		console.warn(`${DIGIO_OVERLAY_LOG_PREFIX} ${context}: viewport sizing threw — continuing without it.`, e);
		return () => { /* no-op */ };
	}
};

// ─── Loader ─────────────────────────────────────────────────────────────────

export const DIGIO_SDK_SRC = `${DIGIO_DIGILOCKER_BASE_URL}/sdk/v11/digio.js`;
const SDK_SCRIPT_ID = "digio-web-sdk-v11";

/** Module-level singleton so concurrent consumers share one injection. */
let sdkPromise: Promise<void> | null = null;

const loadDigioSdk = (): Promise<void> => {
	// Already evaluated on this page — nothing to do.
	if (getDigioConstructor()) return Promise.resolve();
	if (sdkPromise) return sdkPromise;

	sdkPromise = new Promise<void>((resolve, reject) => {
		const existing = document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null;
		const script = existing ?? document.createElement("script");

		const onLoad = () => resolve();
		const onError = () => {
			// Allow a later retry (e.g. transient network failure).
			sdkPromise = null;
			// The dead tag MUST be removed as well. A <script> that has already
			// fired `error` never fires `load`/`error` again, so if it were left
			// in the DOM the NEXT consumer would adopt it through `existing`
			// below, attach listeners to an element that can no longer fire and
			// wait forever: that page would sit on `sdkPending` (trigger
			// permanently disabled, no `sdkError`, so the non-inline fallback
			// never unlocks). Removing it makes the retry inject a fresh tag.
			script.remove();
			reject(new Error(`Failed to load Digio SDK from ${DIGIO_SDK_SRC}`));
		};

		script.addEventListener("load", onLoad, { once: true });
		script.addEventListener("error", onError, { once: true });

		if (!existing) {
			script.id = SDK_SCRIPT_ID;
			script.src = DIGIO_SDK_SRC;
			script.async = true;
			document.body.appendChild(script);
		}
	});

	return sdkPromise;
};

export interface UseDigioSdkResult {
	/** True once `window.Digio` is available. */
	loaded: boolean;
	/** Human-readable load error, or null. */
	error: string | null;
}

/**
 * Injects the Digio Web SDK v11 script once and reports its load state.
 * Safe to call from several components simultaneously.
 */
export const useDigioSdk = (): UseDigioSdkResult => {
	const [loaded, setLoaded] = useState<boolean>(() => Boolean(getDigioConstructor()));
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (loaded) return;

		let mounted = true;
		loadDigioSdk()
			.then(() => {
				if (!mounted) return;
				if (getDigioConstructor()) {
					setLoaded(true);
				} else {
					setError("Digio SDK loaded but window.Digio is unavailable");
				}
			})
			.catch((err: Error) => {
				if (!mounted) return;
				console.error("[Digio SDK]", err);
				setError("Failed to load Digio SDK");
			});

		return () => {
			mounted = false;
		};
	}, [loaded]);

	return { loaded, error };
};

export default useDigioSdk;
